/**
 * Pure helpers for turning a markdown body into the metadata the UI needs.
 * Shared so the file-backed store and the IndexedDB store derive titles
 * identically — a note must not be named differently on desktop and web.
 */

import type { Note, NoteSummary } from './types'

export const UNTITLED = 'Untitled'

/** Strip the markdown syntax that would only be noise in a one-line label. */
function flatten(line: string): string {
  return line
    .replace(/^\s{0,3}#{1,6}\s+/, '') // heading marker
    .replace(/^\s{0,3}>\s?/, '') // blockquote
    .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?/, '') // list / task marker
    .replace(/^\s{0,3}(?:[-*_]\s*){3,}$/, '') // thematic break
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1') // inline code
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links and images
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
    .replace(/(\*|_)(.*?)\1/g, '$2') // italic
    .replace(/~~(.*?)~~/g, '$1') // strikethrough
    .trim()
}

function meaningfulLines(body: string): string[] {
  const lines: string[] = []
  let inFence = false

  for (const raw of body.split('\n')) {
    if (/^\s{0,3}(?:```|~~~)/.test(raw)) {
      inFence = !inFence
      continue
    }
    if (inFence) continue

    const flat = flatten(raw)
    if (flat) lines.push(flat)
  }

  return lines
}

export function deriveTitle(body: string): string {
  const [first] = meaningfulLines(body)
  if (!first) return UNTITLED
  return first.length > 80 ? `${first.slice(0, 79).trimEnd()}…` : first
}

export function derivePreview(body: string): string {
  const rest = meaningfulLines(body).slice(1).join(' ')
  return rest.length > 160 ? `${rest.slice(0, 159).trimEnd()}…` : rest
}

export function toSummary(note: Note): NoteSummary {
  return {
    id: note.id,
    title: deriveTitle(note.body),
    preview: derivePreview(note.body),
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  }
}

/** Build a brand-new note record. Used by every storage backend. */
export function makeNote(body = ''): Note {
  const now = Date.now()
  return {
    id: crypto.randomUUID(),
    body,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    rev: 1,
  }
}

/**
 * Most recently updated first. The UI only ever takes the head of this list —
 * that note is the scratchpad — but the storage backends still sort, so a
 * history view would get the right order for free.
 */
export function byRecency(a: NoteSummary, b: NoteSummary): number {
  return b.updatedAt - a.updatedAt
}
